import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import FormNumberInput from "go-form/components/FormNumberInput";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { StocktakingDraftStateApi } from "../../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../../services/Api/api";

interface Props {
	type: "volume" | "amount" | "value";
	item: StocktakingDraftStateApi;
	stocktakingId: number;
	draftId: number;
	onTableCellInputBlur: (updatedItem: StocktakingDraftStateApi) => void;
}

const DraftStateInput = ({ draftId, item, type, stocktakingId, onTableCellInputBlur }: Props) => {
	const form = useForm<StocktakingDraftStateApi>({
		defaultValues: {
			...item,
		},
		criteriaMode: "all",
	});
	const { addFlash } = useFlash();
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const currency = organization.currency || "PLN";
	const {
		formState,
		formState: { errors },
		control,
		setError,
		clearErrors,
		reset,
	} = form;

	const onBlur = async (newValue?: number) => {
		try {
			clearErrors();
			const data = parseItemData(newValue);
			const res = await api.organization().updateStocktakingDraftState(stocktakingId, draftId, data);
			reset({
				...res,
			});
			onTableCellInputBlur(data);
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
	};

	const parseItemData = (newValue?: number) => {
		switch (type) {
			case "amount":
				return {
					...item,
					amount: newValue,
				};
			case "volume":
				return {
					...item,
					volume: newValue,
				};
			case "value":
				return {
					...item,
					value: newValue !== undefined ? { amount: newValue, currency } : undefined,
				};
		}
	};

	return (
		<FormDirty formState={formState}>
			<div>
				{type === "value" ? (
					<FormMoneyInput
						errors={errors}
						name="value.amount"
						withoutCurrency
						currency=""
						onBlur={onBlur}
						control={control}
						allowEmpty
					/>
				) : (
					<div className="d-flex">
						<FormNumberInput
							errors={errors}
							name={type}
							control={control}
							onBlur={onBlur}
							suffix=""
							allowEmpty
							allowNegative={type === "amount"}
						/>
						{type === "volume" && (
							<span className="text-muted">
								{t(`enums.products.measure_types.${item.product.measure_type}`)}
							</span>
						)}
					</div>
				)}
			</div>
		</FormDirty>
	);
};

export default DraftStateInput;
